/**
 * How many nodes the flat constellation will draw, and which ones.
 *
 * ROUND 13 C6. Clicking every constellation node with the inspector
 * open selected a NEIGHBOUR on 1 of 233 at 1,000 configuration items,
 * 59 of 901 at 5,000 and 174 of 3,031 at 20,000. Ten wrong answers
 * repeated identically, so the resolver is deterministic and the orbs
 * are STACKED rather than adjacent -- the view's own note said 2,840 of
 * 3,031 labels could not be drawn without printing over another.
 *
 * Round 11's nearest-centre hit test is why that is 5.7 per cent and
 * not thirty, and it is the right fix for ADJACENCY. Stacking is a
 * different problem and the fix list says so: it needs a layout answer.
 *
 * THE LAYOUT ANSWER FOR THREE THOUSAND ORBS IN ONE VIEWPORT IS NOT TO
 * DRAW THREE THOUSAND ORBS. `laneLayout` already spills a crowded tier
 * into extra lanes rather than compressing past a floor, and at this
 * volume it is spilling into roughly a hundred lanes, most of them off
 * canvas. A reader cannot see them, cannot label them and cannot click
 * them accurately; the honest thing is to draw the ones that carry the
 * answer and say how many are not there.
 *
 * Kept in the same order the 3D scene keeps them (round 13 C1), because
 * the two views answer the same question and a reader moving between
 * them should not find a different estate.
 */
import type { TwinEdge, TwinNode } from "shared/api/types";
import type { NodeVisualState } from "./nodeState";

/** Matched to the 3D scene's cap so the two modes agree. */
export const CONSTELLATION_NODE_CAP = 400;

export interface CappedNodes {
  nodes: TwinNode[];
  edges: TwinEdge[];
  /** How many nodes were not drawn. 0 when everything fits, so a reader
   *  is never told about a cap that did not happen. */
  hidden: number;
}

const IMPAIRMENT: Record<NodeVisualState, number> = {
  down: 0,
  degraded: 1,
  "at-risk": 2,
  stable: 3,
};

/** Round 14 E1 -- at most this many search matches are brought into
 *  the drawing, so a two-letter search cannot replace the estate. */
export const PINNED_MATCH_LIMIT = 50;

export function capNodes(
  nodes: TwinNode[],
  edges: TwinEdge[],
  states: Map<string, NodeVisualState>,
  cap: number = CONSTELLATION_NODE_CAP,
  /** Round 14 E1 -- nodes that must be drawn whatever their rank: a
   *  search match, the selected node. "Search for one by name" was the
   *  way to a hidden node, and the search only looked at drawn ones. */
  pinned: ReadonlySet<string> = new Set(),
): CappedNodes {
  if (cap <= 0 || nodes.length <= cap) return { nodes, edges, hidden: 0 };

  const degree = new Map<string, number>();
  for (const e of edges) {
    degree.set(e.from_id, (degree.get(e.from_id) ?? 0) + 1);
    degree.set(e.to_id, (degree.get(e.to_id) ?? 0) + 1);
  }

  const rank = (n: TwinNode): number =>
    IMPAIRMENT[states.get(n.id) ?? "stable"] * 1_000_000 +
    (n.mvb_flag ? 0 : 100_000) -
    (degree.get(n.id) ?? 0);

  const pins = nodes.filter((n) => pinned.has(n.id)).slice(0, cap);
  const pinIds = new Set(pins.map((n) => n.id));
  const kept = [
    ...pins,
    ...[...nodes]
      .filter((n) => !pinIds.has(n.id))
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, cap - pins.length),
  ];
  const keptIds = new Set(kept.map((n) => n.id));

  return {
    // Back into the order the view was given them in, so the cap changes
    // WHICH nodes are drawn and never the order they are drawn in.
    nodes: nodes.filter((n) => keptIds.has(n.id)),
    // An edge to a node that is not drawn is a line to nowhere, and
    // reads as a missing node rather than a hidden one.
    edges: edges.filter((e) => keptIds.has(e.from_id) && keptIds.has(e.to_id)),
    hidden: nodes.length - kept.length,
  };
}
